<template>
  <div>
    <!-- if this is active route (e.g. /orders) -->
    <template v-if="isCurrentRoute">
      <entity-entry-header :entityKey="eCurrent.key" :title="$t(`views.${eCurrent.key}.title`)" />

      <entity-entry-table :entityKey="eCurrent.key" :tableSettings="table" searchable>
        <template #cell(orderstate)="data">
          <order-state-badge :statename="data.cell.item.orderstate" :label="data.cell.item.orderstate_label[$i18n.locale]" />
        </template>

        <template #cell(ordersource_label)="data">
          <template v-if="data.cell.item.ordersource_label && data.cell.item.ordersource_label[$i18n.locale]">
            {{ data.cell.item.ordersource_label[$i18n.locale] }}
          </template>
          <template v-else-if="data.cell.item.ordersource && data.definition">
            {{ valueFormatter(data.definition, 'orderSource', data.cell.item.ordersource).value }}
          </template>
        </template>

        <template #cell(requiredactions)="data">
          <div class="badge-group">
            <b-badge v-for="action in data.cell.item.requiredactions" :key="action" variant="warning">{{ action }}</b-badge>
          </div>
        </template>

        <template #cell(orderdate)="data">
          <template v-if="data.cell.item.orderdate">
            {{ formatDate(data.cell.item.orderdate) }}
          </template>
        </template>
      </entity-entry-table>
    </template>

    <!-- if subroute is active route (e.g. /orders/detail) -->
    <section v-else>
      <entity-detail-header :entityKey="eCurrent.key" :entityId="eCurrent.id">
        <template #default="data">
          <template v-if="data.entity && data.entity.orderId">
            <b-row align-v="end">
              <b-col lg>
                <dl class="detailheader-list">
                  <dt class="sr-only" v-if="data.definition && data.definition.$properties && data.definition.$properties.orderId">{{
                    data.definition.$properties.orderId.translations[$i18n.locale].orderId }}</dt>
                  <dd>
                    <h1>{{ data.entity.orderId }}</h1>
                  </dd>

                  <!-- B2B Order Fields -->
                  <template v-if="!isEndCustomerOrder(data.entity)">
                    <div class="w-100"></div>
                    <dt>Standort:</dt>
                    <dd v-if="data.entity.configuration && data.entity.configuration.location">{{ data.entity.configuration.location.name }} <b-badge
                               v-if="data.entity.configuration.ownLocation ? data.entity.configuration.location.id !== data.entity.configuration.ownLocation.id : false"
                               variant="primary">Fremdbestellung</b-badge></dd>

                    <div class="w-100"></div>
                    <dt>GUS-Id:</dt>
                    <dd v-if="data.entity.configuration && data.entity.configuration.location">{{ data.entity.configuration.location.customerId }}</dd>
                  </template>

                  <!-- EndCustomer Order Fields -->
                  <template v-if="isEndCustomerOrder(data.entity)">
                    <div class="w-100"></div>
                    <dt>Mandant:</dt>
                    <dd v-if="data.entity.configuration && data.entity.configuration.tenant">{{ data.entity.configuration.tenant }}</dd>

                    <div class="w-100"></div>
                    <dt>Sprache:</dt>
                    <dd v-if="data.entity.configuration && data.entity.configuration.language">{{ data.entity.configuration.language }}</dd>

                    <div class="w-100"></div>
                    <dt>Zahlungsart:</dt>
                    <dd v-if="data.entity.configuration && data.entity.configuration.paymentType">{{ data.entity.configuration.paymentType }}</dd>
                  </template>

                  <!-- Common Fields -->
                  <div class="w-100"></div>
                  <dt>Quelle:</dt>
                  <dd>{{ data.definition ? valueFormatter(data.definition, 'orderSource', data.entity.configuration ? data.entity.configuration.source : data.entity.orderSource).value :
                    (data.entity.configuration ?
                      data.entity.configuration.source : data.entity.orderSource) }}</dd>

                  <div class="w-100"></div>
                  <dt v-if="data.entity.configuration && data.entity.configuration.user">Benutzer:</dt>
                  <dd v-if="data.entity.configuration && data.entity.configuration.user">{{ data.entity.configuration.user.firstname }} {{ data.entity.configuration.user.lastname }} ({{
                    data.entity.configuration.user.email }})</dd>

                  <div v-if="data.entity.orderedBasket && data.entity.orderedBasket.configuration && data.entity.orderedBasket.configuration.orderReference" class="w-100"></div>
                  <dt v-if="data.entity.orderedBasket && data.entity.orderedBasket.configuration && data.entity.orderedBasket.configuration.orderReference">Referenz</dt>
                  <dd v-if="data.entity.orderedBasket && data.entity.orderedBasket.configuration && data.entity.orderedBasket.configuration.orderReference">{{
                    data.entity.orderedBasket.configuration.orderReference
                  }}</dd>

                  <div v-if="data.entity.orderedBasket && data.entity.orderedBasket.configuration && data.entity.orderedBasket.configuration.orderComments" class="w-100"></div>
                  <dt v-if="data.entity.orderedBasket && data.entity.orderedBasket.configuration && data.entity.orderedBasket.configuration.orderComments">Hinweise zur Bestellung</dt>
                  <dd v-if="data.entity.orderedBasket && data.entity.orderedBasket.configuration && data.entity.orderedBasket.configuration.orderComments" class="text-danger font-weight-bold h4">{{
                    data.entity.orderedBasket.configuration.orderComments }}</dd>
                </dl>
              </b-col>
              <b-col lg class="text-right">
                <dd><strong>Status: </strong> <order-state-badge :statename="data.entity.orderState" :label="$t(`Attribute_OrderState_Values_${data.entity.orderState}`) || data.entity.orderState" />
                </dd>
                <div class="w-100"></div>
                <br />
                <br />
              </b-col>
            </b-row>
          </template>
        </template>
      </entity-detail-header>

      <router-view />
    </section>
  </div>
</template>

<script>
import { CLASS_TABLE_FIELD_KEY_UNKNOWN } from '@/assets/js/config/client'

import { valueFormatter } from '@/assets/js/helper/entity'
import { isEndCustomerOrder } from '@/assets/js/helper/product'
import { TableHelper } from '@/assets/js/helper/table'

import EntityDetailHeader from '@/components/entity/DetailHeader'
import EntityEntryHeader from '@/components/entity/EntryHeader'
import EntityEntryTable from '@/components/entity/EntryTable'
import MixinEntityBase from '@/components/mixins/EntityBase'
import OrderStateBadge from '../../components/order/OrderStateBadge.vue'

import moment from 'moment'

const ENTITY_KEY = 'orders'

export default {
  name: 'Orders',
  mixins: [MixinEntityBase],
  components: {
    EntityEntryHeader,
    EntityEntryTable,
    EntityDetailHeader,
    OrderStateBadge
  },
  data() {
    return {
      entityKey: ENTITY_KEY,
      table: {
        helper: TableHelper,
        mapper: this.tableEntitiesMapper,
        options: {
          fields: {
            includes: [],
            excludes: [],
            sorting: [CLASS_TABLE_FIELD_KEY_UNKNOWN, 'requiredactions'],
            labelKey: `views.${this.$options.name.toLowerCase()}.table.label.{field}`,
            class: {
              requiredactions: 'cell-align-right'
            },
            thClass: {
              requiredactions: 'cell-min cell-nowrap'
            }
          }
        },
        events: {
          rowClicked: this.tableRowClicked
        }
      }
    }
  },
  computed: {
    isCurrentRoute() {
      return Object.values(this.$route.matched[this.$route.matched.length - 1].components).find(c => c.name === this.$options.name) !== undefined
    }
  },
  methods: {
    valueFormatter,
    isEndCustomerOrder,
    tableEntitiesMapper(entities) {
      return entities
        .map(e => {
          return Object.assign({}, e, {
            $routerLink: {
              name: e.ordersource === 'Clarli' ? `Clarli.${this.$options.name}.Overview` : `${this.$options.name}.Detail`,
              params: {
                [this.eCurrent.paramKey]: e.ordernumber || e.id
              }
            }
          })
        })
    },
    tableRowClicked(entity) {
      this.$router.push(entity.$routerLink)
    },
    formatDate(date) {
      return moment(new Date(date)).format('DD.MM.YYYY, HH:mm:ss')
    }
  },
  created() {
    TableHelper.rowClassHook(entity => entity.$routerLink ? 'has-routerlink' : null)
  }
}
</script>

<style lang="scss"></style>
